import { useState } from 'react';
import { Cloud, Loader2, TerminalSquare, Folder, Code2, Shield } from 'lucide-react';
import { api } from '../../services/api';

/**
 * Public landing page — shown before sign-in. Google's OAuth verification
 * requires the homepage to explain the app's purpose and link to a privacy
 * policy WITHOUT requiring login, so all of that lives here.
 */
export function Landing() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getGoogleAuthUrl();
      window.location.href = data.url;
    } catch (err: any) {
      setError(err.message || 'Sign-in failed');
      setLoading(false);
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto bg-gradient-to-br from-indigo-950 via-gray-900 to-slate-900">
      <div className="min-h-full flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          {/* Hero */}
          <div className="flex flex-col items-center text-center mb-10">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/20">
              <Cloud className="w-8 h-8 text-white" />
            </div>              <h1 className="text-3xl font-bold text-white tracking-tight">Shadow Cloud Code</h1>
            <p className="text-white/60 mt-3 max-w-xl">
              A personal cloud coding workspace in your browser. It gives you a remote Linux
              desktop with a real terminal, a code editor and an AI assistant — and keeps
              your project files synced with your own Google Drive, under your control.
            </p>
          </div>

          {/* Features — explains the purpose without login */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
            {[
              { icon: <TerminalSquare className="w-5 h-5 text-cyan-400" />, title: 'Real terminal', desc: 'Full Linux shell on a cloud server — run builds, dev servers and CLI coding agents.' },
              { icon: <Folder className="w-5 h-5 text-amber-400" />, title: 'Your files, your Drive', desc: 'Projects sync to a folder in your own Google Drive. Nothing is stored on our servers permanently.' },
              { icon: <Code2 className="w-5 h-5 text-purple-400" />, title: 'Built-in editor & AI', desc: 'Edit code in the browser with an AI assistant wired to your project workspace.' },
              { icon: <Shield className="w-5 h-5 text-green-400" />, title: 'Private by design', desc: 'Access is limited to approved Google accounts. Drive data is only read/written with your explicit OAuth consent.' },
            ].map((f) => (
              <div key={f.title} className="rounded-xl bg-white/[0.05] border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  {f.icon}
                  <h3 className="text-sm font-semibold text-white">{f.title}</h3>
                </div>
                <p className="text-xs text-white/50 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Sign-in card */}
          <div className="rounded-2xl bg-white/[0.06] backdrop-blur-xl border border-white/10 shadow-2xl p-8">
            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-4">
                {error}
              </div>
            )}
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full py-3 bg-white text-gray-900 font-medium rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              )}
              Sign in with Google
            </button>
            <p className="text-xs text-white/40 text-center mt-4">
              Sign-in is restricted to approved accounts. By continuing you agree that
              your Google Drive will be accessed only to sync your own project files.
            </p>
          </div>

          {/* Required public links */}
          <footer className="flex items-center justify-center gap-4 mt-8 text-xs text-white/40">
            <a href="/privacy" className="underline underline-offset-2 hover:text-white/70">
              Privacy Policy
            </a>
            <span>·</span>
            <span>Contact: mrupala89@gmail.com</span>
          </footer>
        </div>
      </div>
    </div>
  );
}
